import { EventSourceFuncArg, EventSourceInput } from '@fullcalendar/core';
import { ResourceInput } from '@fullcalendar/resource';
import dayjs from 'dayjs';
import { queryClient } from 'src/components/queryProvider';
import { Snackbar } from 'src/components/snackbar';
import { lang, queryKeys, requestCCApi } from 'src/utils';

export interface ScheduleDetailsParams {
  scheduleId?: string;
  assemblyId?: string;
  position: string;
  locationCode?: string;
}

export interface CreateEventSourceParams {
  selectedDaypartId: string;
  locationGroupId: string;
  viewType: string;
  position: string;
  location_code: string;
}

interface CreateResourceSourceParams {
  selectedDaypartId: string;
  locationGroupId: string;
}

export const fetchCalendarEvents = async ({
  start,
  end,
  daypartId,
  locationGroupId,
  viewType,
  position,
  locationCode,
}: {
  start: string;
  end: string;
  daypartId: string;
  locationGroupId: string;
  viewType: string;
  position: string;
  locationCode: string;
}) => {
  const params = new URLSearchParams({
    start: start,
    end: end,
    daypart_id: daypartId,
    location_group_id: locationGroupId,
    viewType: viewType,
    position: position,
    location_code: locationCode,
    currentDate: dayjs().format('YYYY-MM-DD'),
  });

  const url = `/calendarEventsData?${params.toString()}`;

  const response = await requestCCApi({ url });

  return response;
};

export const fetchCalendarResources = async ({
  selectedDaypartId,
  locationGroupId,
}: {
  selectedDaypartId: string;
  locationGroupId: string;
}) => {
  const params = new URLSearchParams({
    location_group_id: locationGroupId,
    daypart_id: selectedDaypartId,
  });

  const url = `/calendarResourcesData?${params.toString()}`;

  const response = await requestCCApi({ url });

  return response;
};

export const getCalendarEventsQueryKey = (
  payload: CreateEventSourceParams,
  fetchInfo: EventSourceFuncArg,
) => {
  const {
    selectedDaypartId,
    locationGroupId,
    viewType,
    position,
    location_code,
  } = payload;

  return queryKeys.getCalendarEvents.details([
    locationGroupId,
    location_code,
    JSON.stringify({
      start: fetchInfo.startStr,
      end: fetchInfo.endStr,
      daypartId: selectedDaypartId,
      viewType,
      position,
    }),
  ]);
};

export const createEventSource = (
  payload: CreateEventSourceParams,
): EventSourceInput => ({
  events: async (fetchInfo, successCallback, failureCallback) => {
    const {
      selectedDaypartId,
      locationGroupId,
      viewType,
      position,
      location_code,
    } = payload;
    const queryKey = getCalendarEventsQueryKey(payload, fetchInfo);

    try {
      const data = await queryClient.fetchQuery({
        queryKey,
        queryFn: () =>
          fetchCalendarEvents({
            start: fetchInfo.startStr,
            end: fetchInfo.endStr,
            daypartId: selectedDaypartId,
            locationGroupId,
            viewType,
            position,
            locationCode: location_code,
          }),
      });

      successCallback(data);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error');
      console.error('Event fetch failed:', error.message);
      failureCallback(error);
    }
  },

  eventDataTransform: (event) => {
    if (event.end && !event.adjustedEnd) {
      event.end = dayjs(event?.end as string | Date)
        .add(1, 'day')
        .format('YYYY-MM-DD');

      event.adjustedEnd = true;
    }

    return event;
  },
});

export const createResourceSource =
  ({ selectedDaypartId, locationGroupId }: CreateResourceSourceParams) =>
  async (
    fetchInfo: unknown,
    successCallback: (resources: ResourceInput[]) => void,
    failureCallback: (error: Error) => void,
  ) => {
    try {
      const data = await fetchCalendarResources({
        selectedDaypartId,
        locationGroupId,
      });
      successCallback(data);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error');
      console.error('Resource fetch failed:', error.message);
      failureCallback(error);
    }
  };

export const fetchScheduleDetails = async (params: ScheduleDetailsParams) => {
  const { scheduleId, assemblyId, position, locationCode } = params;

  if (!scheduleId && !assemblyId) {
    throw new Error('Either scheduleId or assemblyId must be provided.');
  }

  const queryParams = new URLSearchParams({
    ...(scheduleId && { schedule_id: scheduleId }),
    ...(assemblyId && { assembly_id: assemblyId }),
    ...(locationCode && { location_code: locationCode }),
    position,
  });

  const url = `/scheduleDetails?${queryParams.toString()}`;

  const [response] = await requestCCApi({ url });

  return response;
};

export const getSidebarDetailsData = async (params: ScheduleDetailsParams) => {
  try {
    const queryKey = queryKeys.getCalendarSidebarDetails.details([
      JSON.stringify(params),
    ]);

    const response = await queryClient.fetchQuery(queryKey, () =>
      fetchScheduleDetails(params),
    );

    return response;
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    Snackbar.error(`${lang.errorFetchingData} ${message}`);

    return null;
  }
};

export const adjustEndDate = (
  end: string | Date | undefined,
  shouldAdjust: boolean,
): string | Date | undefined => {
  if (!shouldAdjust || !end) {
    return end;
  }

  return dayjs(end as string | Date)
    .subtract(1, 'day')
    .format('YYYY-MM-DD');
};
